import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { startOfDay, endOfDay, subDays, format } from 'date-fns';

export interface AnalyticsMetrics {
  totalSessions: number;
  totalPageViews: number;
  totalEvents: number;
  bounceRate: number;
  avgSessionDuration: number;
  topPages: Array<{ page: string; views: number }>;
  topSources: Array<{ source: string; sessions: number }>;
  dailyStats: Array<{ date: string; sessions: number; pageViews: number }>;
  deviceTypes: Array<{ device: string; sessions: number }>;
  browsers: Array<{ browser: string; sessions: number }>;
  formSubmissions: number;
  topFormTypes: Array<{ form_type: string; submissions: number }>;
  leadSources: Array<{ source: string; leads: number }>;
  // New fields
  totalReservations: number;
  reservationsByProduct: Array<{ product: string; count: number }>;
  reservationsByStatus: Array<{ status: string; count: number }>;
  googleAdsData: {
    sessions: number;
    conversions: number;
    campaigns: Array<{ campaign: string; sessions: number; conversions: number }>;
  };
  countries: Array<{ country: string; sessions: number }>;
  cities: Array<{ city: string; sessions: number }>;
}

const fetchAnalyticsData = async (days: number = 30): Promise<AnalyticsMetrics> => {
  const startDate = startOfDay(subDays(new Date(), days));
  const endDate = endOfDay(new Date());
  const startISO = startDate.toISOString();
  const endISO = endDate.toISOString();

  // Fetch all data in parallel
  const [sessionsRes, eventsRes, formRes, reservationsRes] = await Promise.all([
    supabase.from('analytics_sessions').select('*').gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('analytics_events').select('*').gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('form_submissions').select('*').gte('created_at', startISO).lte('created_at', endISO),
    supabase.from('reservations').select('*').gte('created_at', startISO).lte('created_at', endISO),
  ]);

  const sessions = sessionsRes.data || [];
  const events = eventsRes.data || [];
  const formSubmissions = formRes.data || [];
  const reservations = reservationsRes.data || [];

  const totalSessions = sessions.length;
  const pageViewEvents = events.filter(e => e.event_type === 'page_view');
  const totalPageViews = pageViewEvents.length;
  const totalEvents = events.length;
  
  const bouncedSessions = sessions.filter(s => (s.page_views || 0) <= 1).length;
  const bounceRate = totalSessions > 0 ? (bouncedSessions / totalSessions) * 100 : 0;
  
  const avgSessionDuration = totalSessions > 0
    ? sessions.reduce((acc, s) => acc + (s.duration_seconds || 0), 0) / totalSessions
    : 0;

  // Top pages - safely extract pathname
  const pageViews: Record<string, number> = {};
  pageViewEvents.forEach(e => {
    try {
      const path = e.page_url.startsWith('http') ? new URL(e.page_url).pathname : e.page_url;
      pageViews[path] = (pageViews[path] || 0) + 1;
    } catch {
      pageViews[e.page_url] = (pageViews[e.page_url] || 0) + 1;
    }
  });
  const topPages = Object.entries(pageViews)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([page, views]) => ({ page, views }));

  // Top sources
  const sources: Record<string, number> = {};
  sessions.forEach(s => {
    let source = 'Direct';
    if (s.utm_source) source = s.utm_source;
    else if (s.referrer) {
      try {
        source = new URL(s.referrer).hostname;
      } catch {
        source = s.referrer;
      }
    }
    sources[source] = (sources[source] || 0) + 1;
  });
  const topSources = Object.entries(sources)
    .sort(([,a], [,b]) => b - a)
    .slice(0, 10)
    .map(([source, sessions]) => ({ source, sessions }));

  // Daily stats
  const dailyStats: Record<string, { sessions: number; pageViews: number }> = {};
  for (let i = 0; i < days; i++) {
    const date = format(subDays(new Date(), i), 'yyyy-MM-dd');
    dailyStats[date] = { sessions: 0, pageViews: 0 };
  }
  
  sessions.forEach(s => {
    const date = format(new Date(s.created_at), 'yyyy-MM-dd');
    if (dailyStats[date]) dailyStats[date].sessions++;
  });
  
  pageViewEvents.forEach(e => {
    const date = format(new Date(e.created_at), 'yyyy-MM-dd');
    if (dailyStats[date]) dailyStats[date].pageViews++;
  });

  // Device types
  const devices: Record<string, number> = {};
  sessions.forEach(s => {
    const device = s.device_type || 'Unknown';
    devices[device] = (devices[device] || 0) + 1;
  });

  // Browsers
  const browserMap: Record<string, number> = {};
  sessions.forEach(s => {
    const browser = s.browser || 'Unknown';
    browserMap[browser] = (browserMap[browser] || 0) + 1;
  });

  // Form submissions
  const formTypes: Record<string, number> = {};
  formSubmissions.forEach(f => {
    formTypes[f.form_type] = (formTypes[f.form_type] || 0) + 1;
  });

  // Lead sources
  const leadSourceMap: Record<string, number> = {};
  formSubmissions.forEach(f => {
    let source = 'Direct';
    if (f.utm_source) source = f.utm_source;
    else if (f.referrer) {
      try { source = new URL(f.referrer).hostname; } catch { source = f.referrer; }
    }
    leadSourceMap[source] = (leadSourceMap[source] || 0) + 1;
  });

  // Reservations by product
  const productMap: Record<string, number> = {};
  reservations.forEach(r => {
    productMap[r.product_name] = (productMap[r.product_name] || 0) + 1;
  });

  // Reservations by status
  const statusMap: Record<string, number> = {};
  reservations.forEach(r => {
    statusMap[r.status] = (statusMap[r.status] || 0) + 1;
  });

  // Google Ads data (sessions with utm_source = google and utm_medium = cpc)
  const googleAdsSessions = sessions.filter(s => 
    s.utm_source === 'google' && (s.utm_medium === 'cpc' || s.utm_medium === 'paid')
  );
  const googleAdsFormSubmissions = formSubmissions.filter(f =>
    f.utm_source === 'google' && (f.utm_medium === 'cpc' || f.utm_medium === 'paid')
  );
  const campaignMap: Record<string, { sessions: number; conversions: number }> = {};
  googleAdsSessions.forEach(s => {
    const campaign = s.utm_campaign || 'Sans nom';
    if (!campaignMap[campaign]) campaignMap[campaign] = { sessions: 0, conversions: 0 };
    campaignMap[campaign].sessions++;
  });
  googleAdsFormSubmissions.forEach(f => {
    const campaign = f.utm_campaign || 'Sans nom';
    if (!campaignMap[campaign]) campaignMap[campaign] = { sessions: 0, conversions: 0 };
    campaignMap[campaign].conversions++;
  });

  // Countries & cities
  const countryMap: Record<string, number> = {};
  const cityMap: Record<string, number> = {};
  sessions.forEach(s => {
    if (s.country) countryMap[s.country] = (countryMap[s.country] || 0) + 1;
    if (s.city) cityMap[s.city] = (cityMap[s.city] || 0) + 1;
  });

  return {
    totalSessions,
    totalPageViews,
    totalEvents,
    bounceRate,
    avgSessionDuration,
    topPages,
    topSources,
    dailyStats: Object.entries(dailyStats)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, stats]) => ({ date, ...stats })),
    deviceTypes: Object.entries(devices).map(([device, sessions]) => ({ device, sessions })),
    browsers: Object.entries(browserMap).map(([browser, sessions]) => ({ browser, sessions })),
    formSubmissions: formSubmissions.length,
    topFormTypes: Object.entries(formTypes).sort(([,a], [,b]) => b - a).map(([form_type, submissions]) => ({ form_type, submissions })),
    leadSources: Object.entries(leadSourceMap).sort(([,a], [,b]) => b - a).slice(0, 10).map(([source, leads]) => ({ source, leads })),
    totalReservations: reservations.length,
    reservationsByProduct: Object.entries(productMap).sort(([,a], [,b]) => b - a).map(([product, count]) => ({ product, count })),
    reservationsByStatus: Object.entries(statusMap).map(([status, count]) => ({ status, count })),
    googleAdsData: {
      sessions: googleAdsSessions.length,
      conversions: googleAdsFormSubmissions.length,
      campaigns: Object.entries(campaignMap).map(([campaign, data]) => ({ campaign, ...data })),
    },
    countries: Object.entries(countryMap).sort(([,a], [,b]) => b - a).slice(0, 10).map(([country, sessions]) => ({ country, sessions })),
    cities: Object.entries(cityMap).sort(([,a], [,b]) => b - a).slice(0, 10).map(([city, sessions]) => ({ city, sessions })),
  };
};

export const useAnalyticsData = (days: number = 30) => {
  return useQuery({
    queryKey: ['analytics-data', days],
    queryFn: () => fetchAnalyticsData(days),
    refetchInterval: 60000, // Every 60 seconds
  });
};
